import React, { useState } from 'react';
import { getUserId } from '../../services/authService';
import { updateUserInfo } from '../../services/userService';
import { FaUser, FaEnvelope, FaVenusMars, FaImage, FaUserTag, FaCheck } from 'react-icons/fa';
import { useForm, Controller } from 'react-hook-form';

interface UserInfoFormProps {
  initialData: {
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    profileImageUrl?: string;
    gender?: string | null;
  };
  onSuccess: () => void;
  onCancel?: () => void;
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string;
  gender: string;
}

const UserInfoForm: React.FC<UserInfoFormProps> = ({ initialData, onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(initialData.profileImageUrl || null);

  const { 
    control, 
    handleSubmit, 
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      email: initialData.email || '',
      firstName: initialData.firstName || '',
      lastName: initialData.lastName || '',
      profileImageUrl: initialData.profileImageUrl || '',
      gender: initialData.gender || ''
    }
  });
  
  // Theo dõi thay đổi URL hình ảnh
  const watchProfileImage = watch('profileImageUrl');
  
  // Xác thực và cập nhật hình ảnh xem trước
  React.useEffect(() => {
    if (watchProfileImage && watchProfileImage !== previewImage) {
      const img = new Image();
      img.onload = () => setPreviewImage(watchProfileImage);
      img.onerror = () => setPreviewImage(null);
      img.src = watchProfileImage;
    } else if (!watchProfileImage) {
      setPreviewImage(null);
    }
  }, [watchProfileImage, previewImage]);
  
  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      setSuccessMessage('');
      
      // Lấy userId
      const userId = getUserId();
      
      if (!userId) {
        throw new Error('Không thể xác định người dùng. Vui lòng đăng nhập lại.');
      }
      
      // Chuẩn bị dữ liệu gửi lên
      const updateData = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        profileImageUrl: data.profileImageUrl,
        gender: data.gender || null
      };
      
      // Gọi API cập nhật
      const response = await updateUserInfo(userId, updateData);
      
      if (response.result === 'SUCCESS') {
        // Hiển thị thông báo thành công
        setSuccessMessage('Cập nhật thông tin thành công!');
        
        // Gọi callback thành công
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setErrorMessage(response.message || 'Có lỗi xảy ra khi cập nhật thông tin');
      }
    } catch (error: any) {
      console.error('Lỗi khi cập nhật thông tin:', error);
      setErrorMessage(error.message || 'Có lỗi xảy ra khi cập nhật thông tin');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <form 
      className="user-info-form" 
      onSubmit={handleSubmit(onSubmit)}
      style={{
        maxWidth: '650px',
        margin: '0 auto',
        padding: '20px'
      }}
    >
      {/* Avatar preview */}
      <div className="avatar-container" style={{ textAlign: 'center', marginBottom: '30px' }}>
        <div className="avatar-preview" style={{ 
          display: 'inline-block',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          overflow: 'hidden',
          border: '2px solid #20757A',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          {previewImage ? (
            <img 
              src={previewImage} 
              alt="Avatar preview" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover' 
              }} 
            />
          ) : (
            <div className="avatar-placeholder" style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#f4f4f4'
            }}>
              <FaUser size={60} color="#20757A" />
            </div>
          )}
        </div>
        <div className="form-group" style={{ display: 'none' }}>
          <label htmlFor="profileImageUrl">
            <FaImage className="icon" /> URL Ảnh đại diện
          </label>
          <Controller
            name="profileImageUrl"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                id="profileImageUrl"
                placeholder="Nhập URL ảnh đại diện"
                className="hover-effect"
              />
            )}
          />
        </div>
      </div>

      {/* Thông báo thành công */}
      {successMessage && (
        <div className="message-box success mb-md" style={{ textAlign: 'center' }}>
          <FaCheck className="success-icon" /> {successMessage}
        </div>
      )}
      
      {/* Thông báo lỗi */}
      {errorMessage && (
        <div className="message-box error mb-md" style={{ textAlign: 'center' }}>
          {errorMessage}
        </div>
      )}
      
      <div className="form-group" style={{ marginBottom: '20px' }}>
        <label htmlFor="email" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '25%', 
          float: 'left',
          fontSize: '16px'
        }}>
          Email <span className="required"></span>
        </label>
        <div style={{ width: '100%', float: 'right' }}>
          <Controller
            name="email"
            control={control}
            rules={{ 
              required: 'Email không được để trống',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Email không hợp lệ'
              } 
            }}
            render={({ field }) => (
              <input
                {...field}
                type="email"
                id="email"
                autoComplete="email"
                placeholder="Nhập địa chỉ email"
                className="hover-effect"
                style={{ 
                  width: '100%', 
                  padding: '12px 15px', 
                  fontSize: '16px',
                  borderRadius: '6px',
                  border: '1px solid #ddd'
                }}
              />
            )}
          />
          {errors.email && <div className="error-text">{errors.email.message}</div>}
        </div>
        <div style={{ clear: 'both' }}></div>
      </div>
      
      {/* Họ */}
      <div className="form-group" style={{ marginBottom: '20px' }}>
        <label htmlFor="lastName" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '25%', 
          float: 'left',
          fontSize: '16px'
        }}>
          Họ
        </label>
        <div style={{ width: '100%', float: 'right' }}>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                id="lastName"
                autoComplete="family-name"
                placeholder="Nhập họ của bạn"
                className="hover-effect"
                style={{ 
                  width: '100%', 
                  padding: '12px 15px', 
                  fontSize: '16px',
                  borderRadius: '6px',
                  border: '1px solid #ddd'
                }}
              />
            )}
          />
        </div>
        <div style={{ clear: 'both' }}></div>
      </div>
      
      {/* Tên */}
      <div className="form-group" style={{ marginBottom: '20px' }}>
        <label htmlFor="firstName" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '25%', 
          float: 'left',
          fontSize: '16px'
        }}>
          Tên
        </label>
        <div style={{ width: '100%', float: 'right' }}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                id="firstName"
                autoComplete="given-name"
                placeholder="Nhập tên của bạn"
                className="hover-effect"
                style={{ 
                  width: '100%', 
                  padding: '12px 15px', 
                  fontSize: '16px',
                  borderRadius: '6px',
                  border: '1px solid #ddd'
                }}
              />
            )}
          />
        </div>
        <div style={{ clear: 'both' }}></div>
      </div>
      
      <div className="form-group" style={{ marginBottom: '20px' }}>
        <label htmlFor="gender" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '25%', 
          float: 'left',
          fontSize: '16px'
        }}>
          Giới tính
        </label>
        <div style={{ width: '100%', float: 'right' }}>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                id="gender"
                className="hover-effect"
                style={{ 
                  width: '100%', 
                  padding: '12px 15px', 
                  fontSize: '16px', 
                  height: '48px',
                  borderRadius: '6px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="">-- Chọn giới tính --</option>
                <option value="MALE">Nam</option>
                <option value="FEMALE">Nữ</option>
                <option value="OTHER">Khác</option>
              </select>
            )}
          />
        </div>
        <div style={{ clear: 'both' }}></div>
      </div>

      {/* Nút submit */}
      <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
        <button 
          type="button" 
          className="btn-secondary"
          onClick={onCancel || (() => window.history.back())}
          style={{ 
            minWidth: '120px', 
            padding: '12px 15px',
            borderRadius: '6px',
            fontSize: '16px',
            border: 'none',
            backgroundColor: '#f4f4f4',
            cursor: 'pointer'
          }}
        >
          Hủy bỏ
        </button>
        <button 
          type="submit" 
          className="btn-primary scale-effect"
          disabled={isSubmitting}
          style={{ 
            minWidth: '120px', 
            padding: '12px 15px',
            borderRadius: '6px',
            fontSize: '16px',
            border: 'none',
            backgroundColor: '#20757A',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          {isSubmitting ? 'Đang cập nhật...' : 'Lưu thay đổi'}
        </button>
      </div>
    </form>
  );
};

export default UserInfoForm; 